import { useState, type ReactNode } from "react";
import { customFetch } from "@workspace/api-client-react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Percent } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { readAccess, canAccessPage } from "@/lib/access";
import { useAllUnitsMode } from "@/components/layouts/AdminLayout";
import { BirthdaySection } from "@/components/admin/BirthdaySection";
import { trackEvent } from "@/hooks/use-tracking";
import { formatCurrency, orderDisplayCode } from "@/lib/utils";
import {
  ShoppingBag,
  TrendingUp,
  Clock,
  AlertCircle,
  AlertTriangle,
  CalendarDays,
  CalendarCheck,
  Package,
  Building2 as Building2Icon,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { Link, useLocation } from "wouter";
import { PlanPicker, priceCents } from "@/components/PlanPicker";
import { InstallAppButton } from "@/components/InstallAppButton";
import { PublicLinkCard } from "@/components/PublicLinkCard";
import { AvisoAppDesatualizado } from "@/components/admin/AvisoAppDesatualizado";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

// Gráfico de receita com período selecionável. Agrega as RECEITAS pagas do
// Financeiro no client (7/30 dias por dia; 12 meses por mês) — mesma fonte
// da página Financeiro, então os números batem.
function RevenueChart() {
  const [chartPeriod, setChartPeriod] = useState<"7d" | "30d" | "12m">("7d");
  const now = new Date();
  const isoLocal = (d: Date) => new Intl.DateTimeFormat("en-CA").format(d);
  const from = (() => {
    const d = new Date();
    if (chartPeriod === "7d") d.setDate(d.getDate() - 6);
    else if (chartPeriod === "30d") d.setDate(d.getDate() - 29);
    else { d.setMonth(d.getMonth() - 11); d.setDate(1); }
    return isoLocal(d);
  })();

  const { data: txResp } = useQuery({
    queryKey: ["dashboard-revenue-chart", chartPeriod, from],
    queryFn: () => customFetch<{ transactions: Array<{ date: string; amount: string; type: string; status?: string }> }>(
      `/api/financial/transactions?type=receita&dateFrom=${from}&dateTo=${isoLocal(now)}`,
    ),
  });

  const data = (() => {
    const txs = (txResp?.transactions ?? []).filter((t) => t.status !== "pendente");
    if (chartPeriod === "12m") {
      const buckets: Array<{ date: string; revenue: number; label: string }> = [];
      const d = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      for (let i = 0; i < 12; i++) {
        const ym = isoLocal(d).slice(0, 7);
        buckets.push({ date: ym, revenue: 0, label: d.toLocaleDateString("pt-BR", { month: "short" }) });
        d.setMonth(d.getMonth() + 1);
      }
      for (const t of txs) {
        const b = buckets.find((x) => x.date === t.date.slice(0, 7));
        if (b) b.revenue += parseFloat(t.amount);
      }
      return buckets;
    }
    const days = chartPeriod === "7d" ? 7 : 30;
    const buckets: Array<{ date: string; revenue: number; label: string }> = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const iso = isoLocal(d);
      buckets.push({
        date: iso,
        revenue: 0,
        label: chartPeriod === "7d"
          ? d.toLocaleDateString("pt-BR", { weekday: "short" })
          : String(d.getDate()),
      });
    }
    for (const t of txs) {
      const b = buckets.find((x) => x.date === t.date);
      if (b) b.revenue += parseFloat(t.amount);
    }
    return buckets;
  })();

  return (
    <div>
      <div className="flex justify-end">
        <select
          className="border rounded-md p-1.5 text-xs bg-background"
          value={chartPeriod}
          onChange={(e) => setChartPeriod(e.target.value as any)}
          data-testid="dashboard-chart-period"
        >
          <option value="7d">Últimos 7 dias</option>
          <option value="30d">Últimos 30 dias</option>
          <option value="12m">Últimos 12 meses</option>
        </select>
      </div>
      <MockChart bars={data} compact={chartPeriod === "30d"} />
    </div>
  );
}

// Chart showing revenue bars (labels calculados pelo chamador).
function MockChart({ bars, compact }: { bars: Array<{ date: string; revenue: number; label: string }>; compact?: boolean }) {
  const data = bars || [];
  const maxRevenue = Math.max(...data.map(d => d.revenue), 1);
  
  return (
    <div className={`w-full mt-4 flex items-end justify-between px-2 ${compact ? "gap-0.5" : "gap-2"}`} style={{ height: 220 }}>
      {data.map((item) => {
        const ratio = item.revenue / maxRevenue;
        const barHeight = Math.round(ratio * 200);

        return (
          <div key={item.date} className="flex-1 flex flex-col items-center justify-end min-w-0">
            <div className="w-full relative group">
              <div
                className="w-full bg-secondary/30 rounded-t-md hover:bg-secondary/50 transition-colors"
                style={{ height: barHeight > 0 ? `${barHeight}px` : "0px", minHeight: item.revenue > 0 ? 4 : 0 }}
              />
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                {formatCurrency(item.revenue)}
              </div>
            </div>
            <span className={`text-muted-foreground mt-1 truncate ${compact ? "text-[8px]" : "text-[11px]"}`}>{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// Aviso de contas vencendo/vencidas (Financeiro) — aparece logo abaixo do
// faturamento do dia; clique leva pra seção de contas a pagar.
function DuePaymentsBanner() {
  const { data } = useQuery<{ overdueCount?: number; dueTodayCount?: number }>({
    queryKey: ["dashboard-due-payments"],
    queryFn: () => customFetch("/api/financial/summary"),
    staleTime: 60_000,
  });
  const overdue = data?.overdueCount ?? 0;
  const dueToday = data?.dueTodayCount ?? 0;
  if (overdue === 0 && dueToday === 0) return null;
  return (
    <Link href="/financeiro" className="block">
      <div className="flex items-center gap-2 text-xs bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 rounded-lg px-3 py-2 hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors" data-testid="dashboard-due-banner">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="flex-1">
          {overdue > 0 && <>{overdue} conta{overdue > 1 ? "s" : ""} <strong>vencida{overdue > 1 ? "s" : ""}</strong>. </>}
          {dueToday > 0 && <>{dueToday} conta{dueToday > 1 ? "s" : ""} vence{dueToday > 1 ? "m" : ""} <strong>hoje</strong>.</>}
          {" "}Toque pra ver no Financeiro.
        </span>
      </div>
    </Link>
  );
}

function usePublicLinks() {
  const { user } = useAuth();
  const origin = typeof window !== "undefined" ? window.location.origin.replace("://www.", "://") : "";
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
  const catalogUrl = user?.store?.slug ? `${origin}${basePath}/loja/${user.store.slug}` : "";
  const agendaUrl = user?.store?.slug ? `${origin}${basePath}/agendar/${user.store.slug}` : "";
  return { catalogUrl, agendaUrl };
}

function CardWrapper({ href, children }: { href?: string; children: ReactNode }) {
  if (href) {
    return <Link href={href} className="block">{children}</Link>;
  }
  return <div className="block">{children}</div>;
}

export default function Dashboard() {
  const allUnitsMode = useAllUnitsMode();
  // Quem não tem a página Financeiro (Equipe) não vê faturamento aqui também:
  // o card e o gráfico são a mesma informação por outra porta.
  const { user: meForAccess } = useAuth();
  const showFinance = canAccessPage(readAccess(meForAccess), "financeiro");
  const { data: stats, isLoading } = useQuery({
    queryKey: ["/api/dashboard/stats", allUnitsMode ? "group" : "single"],
    queryFn: () => customFetch<Record<string, unknown>>(
      `/api/dashboard/stats${allUnitsMode ? "?scope=group" : ""}`,
      { method: "GET" },
    ),
  });
  const { catalogUrl, agendaUrl } = usePublicLinks();
  const { user } = useAuth();
  const { data: storeSettings } = useQuery<any>({
    queryKey: ["settings"],
    queryFn: () => customFetch("/api/settings"),
    staleTime: 60_000,
  });
  const orderCodeStyle = (storeSettings as any)?.orderCodeStyle as string | undefined;
  const [, setLocation] = useLocation();
  const [subscribing, setSubscribing] = useState(false);
  const [subscribingPlan, setSubscribingPlan] = useState(false);
  const { toast } = useToast();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-[400px] lg:col-span-2 rounded-xl" />
          <Skeleton className="h-[400px] rounded-xl" />
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <AvisoAppDesatualizado />
      <div className="flex justify-end">
        <InstallAppButton />
      </div>
      {allUnitsMode && (
        <div className="flex items-center gap-3 pb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-primary flex items-center justify-center shadow-md">
            <Building2Icon className="text-white w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Todas as unidades</h1>
            <p className="text-sm text-muted-foreground">Visão consolidada de todas as unidades</p>
          </div>
        </div>
      )}
      {!allUnitsMode && <div className="flex flex-col gap-4 w-full shrink-0">
          {stats.storeType !== "servicos" && (
            <PublicLinkCard
              url={catalogUrl}
              label="Link da Loja:"
              icon={<Package className="w-4 h-4 text-secondary" />}
              shareLabel="nossa loja digital"
            />
          )}
          {(stats.storeType === "servicos" || stats.storeType === "ambos") && (
            <PublicLinkCard
              url={agendaUrl}
              label="Link da Agenda:"
              icon={<CalendarDays className="w-4 h-4 text-secondary" />}
              shareLabel="nossa agenda de agendamentos"
              className={stats.storeType === "ambos" ? "pt-4 border-t border-secondary/30" : ""}
            />
          )}
      </div>}
      {!allUnitsMode && stats.plan === 'gratis' && (
        <PlanPicker
          used={stats.monthOrderCount}
          freeOrderLimit={stats.freeOrderLimit || 30}
          nextResetDate={stats.nextOrderResetDate ? parseISO(stats.nextOrderResetDate) : null}
          subscribing={subscribingPlan}
          showProducts={stats.storeType === "produtos" || stats.storeType === "ambos"}
          showServices={stats.storeType === "servicos" || stats.storeType === "ambos"}
          printerConnected={false}
          showUpgradeCard={false}
          onUpgradeClick={() => setLocation("/configuracoes#assinar")}
          onSubscribe={async (plan, cycle) => {
            const valueReais = priceCents(plan, cycle) / 100;
            trackEvent("InitiateCheckout", user?.user?.id, valueReais);
            setLocation(`/assinar?cycle=${cycle}&plan=${plan}`);
          }}
        />
      )}
      {stats.storeType !== "servicos" && (
        <div className="grid grid-cols-2 gap-4">
          <CardWrapper href={allUnitsMode ? undefined : "/pedidos"}>
            <Card className="p-4 flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer h-full">
              <div className="w-10 h-10 shrink-0 rounded-full bg-secondary/15 text-secondary flex items-center justify-center">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold">{stats.todayOrders}</p>
                <p className="text-xs text-muted-foreground">Pedidos Hoje</p>
              </div>
            </Card>
          </CardWrapper>
          <CardWrapper href={allUnitsMode ? undefined : "/pedidos?status=novo"}>
            <Card className="p-4 flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer h-full">
              <div className="w-10 h-10 shrink-0 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center">
                <Clock className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold">{stats.pendingOrders}</p>
                <p className="text-xs text-muted-foreground">Pedidos Pendentes</p>
              </div>
            </Card>
          </CardWrapper>
        </div>
      )}
      {stats.storeType !== "servicos" && (stats as any).menuVisits && (() => {
        // Visitas ao cardápio (10/09/2026): hoje + 7 dias, e quantas viraram
        // pedido do link. Só produto: serviço agenda por outra página.
        const mv = (stats as any).menuVisits as { today: number; week: number; weekLinkOrders: number };
        const rate = mv.week > 0 ? Math.round((mv.weekLinkOrders / mv.week) * 100) : null;
        return (
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-4 flex items-center gap-3 h-full" data-testid="dash-visits">
              <div className="w-10 h-10 shrink-0 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 flex items-center justify-center">
                <Eye className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold">{mv.today}</p>
                <p className="text-xs text-muted-foreground">Visitas hoje · {mv.week} em 7 dias</p>
              </div>
            </Card>
            <Card className="p-4 flex items-center gap-3 h-full" data-testid="dash-conversion">
              <div className="w-10 h-10 shrink-0 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 flex items-center justify-center">
                <Percent className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold">{rate == null ? "—" : `${rate}%`}</p>
                <p className="text-xs text-muted-foreground">Visitas que viraram pedido</p>
              </div>
            </Card>
          </div>
        );
      })()}
      {(stats.storeType === "servicos" || stats.storeType === "ambos") && (
        <div className="grid grid-cols-2 gap-4">
          <CardWrapper href={allUnitsMode ? undefined : "/agenda"}>
            <Card className="p-4 flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer h-full">
              <div className="w-10 h-10 shrink-0 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 flex items-center justify-center">
                <CalendarDays className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold">{stats.todayAppointments || 0}</p>
                <p className="text-xs text-muted-foreground">Agendamentos Hoje</p>
              </div>
            </Card>
          </CardWrapper>
          <CardWrapper href={allUnitsMode ? undefined : "/agenda?status=agendado"}>
            <Card className="p-4 flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer h-full">
              <div className="w-10 h-10 shrink-0 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center">
                <CalendarCheck className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold">{stats.pendingAppointments || 0}</p>
                <p className="text-xs text-muted-foreground">Agendamentos Pendentes</p>
              </div>
            </Card>
          </CardWrapper>
        </div>
      )}
      {showFinance && (
      <CardWrapper href={allUnitsMode ? undefined : "/financeiro"}>
        <Card className="p-4 flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer">
          <div className="w-10 h-10 shrink-0 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 flex items-center justify-center">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-xl font-bold truncate">{formatCurrency(stats.todayRevenue)}</p>
            <p className="text-xs text-muted-foreground">Faturamento Hoje</p>
          </div>
        </Card>
      </CardWrapper>
      )}

      {!!(user?.store as any)?.birthdayReminders && (
        <BirthdaySection
          next={(user?.store as any)?.birthdayAdvanceDays ?? 3}
          past={(user?.store as any)?.birthdayPastDays ?? 2}
          message={(user?.store as any)?.birthdayWhatsappMessage}
        />
      )}

      <DuePaymentsBanner />

      <div className={`grid grid-cols-1 ${stats.storeType === "ambos" ? "lg:grid-cols-2" : ""} gap-3`}>
        {stats.storeType !== "servicos" && (
          <Card className="p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Pedidos Recentes</h3>
              {!allUnitsMode && <Link href="/pedidos" className="text-sm text-secondary hover:underline font-medium">Ver todos</Link>}
            </div>
            <div className="flex-1 overflow-y-auto pr-2 space-y-3">
              {stats.recentOrders.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-8">
                  <ShoppingBag className="w-12 h-12 mb-3 opacity-20" />
                  <p>Nenhum pedido recente</p>
                </div>
              ) : (
                stats.recentOrders.slice(0, 5).map((order: any) => (
                  <div key={order.id} className="p-3 rounded-lg border border-border/50 bg-muted/20 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-sm truncate">{order.customerName || `#${orderDisplayCode(order, orderCodeStyle)}`}</p>
                      <p className="font-bold text-sm text-primary ml-2 shrink-0 mr-[1px]">{formatCurrency(order.total)}</p>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-muted-foreground">{format(parseISO(order.createdAt), "HH:mm", { locale: ptBR })}</p>
                      <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-background border">
                        {order.status === 'novo' ? 'Novo' : order.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        )}

        {(stats.storeType === "servicos" || stats.storeType === "ambos") && (
          <Card className="p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Agendamentos Recentes</h3>
              {!allUnitsMode && <Link href="/agenda" className="text-sm text-secondary hover:underline font-medium">Ver todos</Link>}
            </div>
            <div className="flex-1 overflow-y-auto pr-2 space-y-3">
              {(stats.recentAppointments || []).length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-8">
                  <CalendarDays className="w-12 h-12 mb-3 opacity-20" />
                  <p>Nenhum agendamento recente</p>
                </div>
              ) : (
                (stats.recentAppointments || []).slice(0, 5).map((appt: any) => (
                  <div key={appt.id} className="p-3 rounded-lg border border-border/50 bg-muted/20 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-sm truncate">{appt.customerName || "Cliente"}</p>
                      <p className="font-medium text-xs text-secondary ml-2 shrink-0">{appt.serviceName || "Serviço"}</p>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-muted-foreground">{appt.date} às {appt.time?.slice(0, 5)}</p>
                      <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-background border">
                        {appt.status === 'agendado' ? 'Agendado' : appt.status === 'concluido' ? 'Concluído' : appt.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        )}
      </div>
      {showFinance && (
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">Receita dos Últimos 7 Dias</h3>
        </div>
        <RevenueChart />
      </Card>
      )}
    </div>
  );
}
